import {
  CompassIcon,
  GitBranchIcon,
  GraduationCapIcon,
  HardHatIcon,
  MountainIcon,
  MoveVerticalIcon,
  RulerIcon,
  SatelliteDishIcon,
  WrenchIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Surveying & Topography Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to surveying can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Surveying and topography fundamentals',
      'Essential terminology and concepts',
      'Measurement systems and units',
      'Accuracy and precision',
      'Error sources and management',
      'Coordinate systems and scales',
      'Basic field procedures',
      'Clear explanations with real-world examples',
    ],
    result:
      'Your students understand surveying as a practical science that forms the foundation for construction, infrastructure and land management — not an impossible list of terms to memorize.',
    icon: RulerIcon,
  },
  {
    number: '2',
    title: 'Distance, Angle & Elevation Measurements: Taking Field Data',
    subtitle: 'Everything your students need to know about measuring distances, angles and elevations',
    context:
      'This is where many students struggle. This volume gives you the materials to help them truly understand how surveyors collect accurate field measurements.',
    items: [
      'Distance measurement methods and techniques',
      'Angle and bearing measurements',
      'Azimuth calculations and conversions',
      'Elevation and slope measurements',
      'Grade and gradient calculations',
      'Measurement errors and corrections',
      'Field procedures and reliability',
      '15 worked measurement calculation examples',
    ],
    result: 'Your students learn to take accurate field measurements with method and confidence.',
    icon: CompassIcon,
  },
  {
    number: '3',
    title: 'Leveling & Elevation Surveying: Determining Heights',
    subtitle: 'How to teach leveling principles and elevation surveying clearly and practically',
    context:
      'Leveling is fundamental but complex. This volume gives you everything needed so your students understand how surveyors determine elevations and create profiles.',
    items: [
      'Leveling principles and procedures',
      'Datums and benchmarks',
      'Backsight, foresight and height of instrument',
      'Differential leveling techniques',
      'Rise and fall method',
      'Misclosure calculations and adjustments',
      'Profiles and cross-sections',
      '12 practical leveling case studies ready for classroom use',
    ],
    result:
      'Your students understand why elevation surveying is essential and how to conduct accurate leveling procedures.',
    icon: MoveVerticalIcon,
  },
  {
    number: '4',
    title: 'Traversing, Coordinates & Survey Calculations: Positioning Points',
    subtitle: 'Understanding coordinate systems and calculating survey positions',
    context: 'Traversing and coordinates are complex but fundamental. This volume breaks them down without losing clarity.',
    items: [
      'Coordinate systems and reference frames',
      'Bearings and azimuths in surveying',
      'Traverse procedures and field methods',
      'Latitude and departure calculations',
      'Coordinate calculations and conversions',
      'Traverse closure and misclosure',
      'Adjustment methods and techniques',
      '10 practical traverse calculation case studies',
    ],
    result: 'Your students understand how surveyors position points and calculate accurate coordinates.',
    icon: GitBranchIcon,
  },
  {
    number: '5',
    title: 'Surveying Instruments & Field Procedures: Using Survey Equipment',
    subtitle: 'Introduction to surveying instruments and proper field procedures',
    context:
      'Understanding how to operate surveying instruments properly is crucial. This volume teaches instrument operation and field procedures clearly.',
    items: [
      'Surveying instrument types and functions',
      'Levels: operation and setup',
      'Theodolites and angle measurement',
      'Total stations: capabilities and use',
      'Prisms and reflective targets',
      'GNSS receivers and antenna setup',
      'Station setup and centering',
      'Data collection and field notebooks',
      'Troubleshooting and quality control',
      '10 instrument operation case studies',
    ],
    result: 'Your students understand how to properly operate surveying instruments and conduct quality field work.',
    icon: WrenchIcon,
  },
  {
    number: '6',
    title: 'GNSS, GPS & Modern Surveying Technologies: Satellite Positioning',
    subtitle: 'Satellite-based surveying and modern positioning technologies',
    context:
      'GNSS and GPS are transforming surveying. This volume gives you confidence to teach modern positioning technologies clearly.',
    items: [
      'GNSS and GPS fundamentals',
      'Satellite constellation and signal reception',
      'Positioning principles and accuracy',
      'Real-time Kinematic (RTK) positioning',
      'Static and rapid static surveys',
      'Differential GPS and corrections',
      'Modern surveying workflows',
      'Accuracy considerations and quality assurance',
      '8 GNSS surveying application case studies',
    ],
    result: 'Your students understand how satellite-based surveying works and when to use GPS and GNSS technologies.',
    icon: SatelliteDishIcon,
  },
  {
    number: '7',
    title: 'Topographic Surveys, Contours & Terrain Representation: Mapping Terrain',
    subtitle: 'Creating topographic surveys and representing terrain accurately',
    context:
      'Topographic surveys capture terrain complexity. This volume connects survey data to meaningful terrain representation.',
    items: [
      'Topographic survey planning and execution',
      'Terrain point collection and elevation data',
      'Contour line concepts and interpretation',
      'Contour interval selection',
      'Profile development from surveys',
      'Digital elevation models',
      'Terrain analysis and slope representation',
      '12 topographic mapping case studies',
    ],
    result: 'Your students understand how surveyors create accurate topographic representations of terrain.',
    icon: MountainIcon,
  },
  {
    number: '8',
    title: 'Applied Surveying — Construction, Roads & Land Projects: Real-World Applications',
    subtitle: 'Surveying applications in construction, infrastructure and land development',
    context:
      'This is the practical heart. Your students learn how surveying applies to real construction and infrastructure projects.',
    items: [
      'Construction staking and layout surveying',
      'Setting grades and elevations on site',
      'Road surveying and alignment procedures',
      'Building layout and foundation staking',
      'Land subdivision and boundary surveying',
      'Earthwork calculations and volume estimates',
      'Site plan interpretation',
      'Quality control and verification procedures',
      '15 real-world applied surveying case studies',
    ],
    result:
      'Your students understand how surveying supports construction, roads and infrastructure projects in the real world.',
    icon: HardHatIcon,
  },
  {
    number: '9',
    title: 'Teaching Surveying & Topography: Classroom Excellence',
    subtitle: 'Your complete guide as a surveying educator',
    context:
      'This volume gives you exactly what you need to plan, organize and evaluate your entire year of surveying teaching.',
    items: [
      'The educational value of surveying in technical curricula',
      'Establishing clear learning objectives',
      'Organizing surveying content by difficulty level',
      'Designing effective lesson sequences',
      'Inquiry-based surveying education approaches',
      'Problem-based and project-based learning strategies',
      'Using real surveying case studies in the classroom',
      'Teaching through local surveying projects',
      'Interdisciplinary connections and links',
      'Classroom discussions and guided debates',
      'Laboratory activities and calculations',
      'Field activities and instrument practice',
      'Assessment strategies for surveying learning',
      'Rubrics and performance criteria',
      'Addressing common surveying misconceptions',
      'Differentiation and inclusive instruction',
      '10 complete integrated surveying projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
